import { useFormik } from "formik"
import globalMU from "globalMU"
import * as R from 'ramda'
import React, { Suspense, useState, useCallback, useMemo } from "react"
import { InputMascara } from "../inputs/InputMascara"
import { InputNumerico } from "../inputs/InputNumerico"

const estiloFichaCrediario = require("./ficha-crediario.module.scss").default

export type OpcaoDadoAdiconal = {
  Codigo: number
  Nome: string
}

export type DadosFichaCrediario = {
  CodigoDadoAdicional: number
  Descricao: string
  Tipo: string
  Valor: string
  Opcao: OpcaoDadoAdiconal[] // 465030
}

export type FichaCrediarioProps = {
  cpfCnpj: string
  nome: string
  onSalvar: (dadosFormulario: any, codigosAlterados: number[]) => void
  dadosFichaCrediario: DadosFichaCrediario[]
  somenteLeitura?: boolean // 430731
  processando?: boolean // 430731
}

export const FichaCrediario = (props: FichaCrediarioProps) => {
  const [codigosAlterados, setCodigosAlterados] = useState<number[]>([])

  // Otimização: useMemo para evitar recálculos desnecessários
  const initialValues = useMemo(
    () => R.fromPairs(props.dadosFichaCrediario.map(f => R.pair(f.CodigoDadoAdicional, f.Valor))),
    [props.dadosFichaCrediario]
  )

  const formik = useFormik({
    initialValues,
    onSubmit: () => {}, // Mantido vazio conforme original
  })

  // Otimização: useCallback para evitar recriação de funções
  const handleFieldChange = useCallback(
    (fieldName: string, tipo: string, value: string | number) => {
      const codigo = parseInt(fieldName, 10)
      if (tipo?.toString() == "1014") { // Numérico
        value = value.toString().replace(/[^0-9,]/g, "") //deixar so numeros e virgula
        //deixar somente a primera virgula ou ponto
        const partes = value.split(/[,\.]/)
        if (partes.length > 2) {
          value = partes[0] + "," + partes[1].substring(0, 2)
        }
      }
      setCodigosAlterados(prev => [...prev, codigo])
      formik.setFieldValue(fieldName, value)
    },
    [formik]
  )

  const handleComboChange = useCallback( // 465030
    (fieldName: string, value: string | number, texto: string) => {
      const codigo = parseInt(fieldName, 10)
      setCodigosAlterados(prev => [...prev, codigo])
      formik.setFieldValue(fieldName, value)
    },
    [formik]
  )

  // Otimização: Componente separado para renderização de campos
  const renderField = (value: DadosFichaCrediario) => {
    const fieldKey = value.CodigoDadoAdicional.toString()
    const commonProps = {
      // Sem `key` aqui de proposito: este objeto e espalhado ({...commonProps}) e o React
      // recusa `key` vinda de spread. Era redundante tambem - a chave da lista ja esta no
      // <React.Fragment key={...}> que envolve a chamada de renderField.
      id: `FichaDeCrediarioCliente_${value.Tipo}_${fieldKey}`,
      disabled: props.somenteLeitura,
      onValueChange: (val: string | number) => handleFieldChange(fieldKey, value.Tipo, val),
      value: formik.values[fieldKey] ?? "",
    }

    switch (value.Tipo?.toString()) {
      case "1014": // Numérico
        return (
          <InputNumerico
            {...commonProps}
            style={{ height: "25px" }}
            title="Informe um valor numérico"
            alinharADireita={false}
            valor={Number(
              globalMU.corrigirPontosEVirgulasValorDecimal(
                formik.values[fieldKey]?.toString() ?? "0"
              )
            )}
            numeroCasasDecimais={2}
            selecionaValorOnFocus={true}
          />
        )
      // case "1015": // Data com máscara
      //   return (
      //       <InputMascara
      //         disabled={props.somenteLeitura}
      //         placeholder="Informe uma data"
      //         mascara="99/99/9999"
      //         onValorModificado={(valor) => {
      //           handleFieldChange(fieldKey, value.Tipo, value.CodigoDadoAdicional.toString())
      //         }}
      //         valor={formik.values[value.CodigoDadoAdicional.toString()] ?? ""}
      //         emitirComMascara={true}
      //       />
      //   )
      case "1016": // Texto Longo
        return (
          <textarea
            style={{ paddingTop: "5px" }}
            name={fieldKey}
            id={`FichaDeCrediarioCliente_TextoLongo_${fieldKey}`}
            disabled={props.somenteLeitura}
            onChange={(e) => {
              handleFieldChange(fieldKey, value.Tipo, e.target.value)
            }}
            value={formik.values[fieldKey] ?? ""}
          />
        )
      default: // Texto simples
        if (value.Opcao?.length > 0) { 
          return (
            <select
              style={{ paddingTop: "5px", height: "25px" }}
              name={fieldKey}
              id={`FichaDeCrediarioCliente_Select_${fieldKey}`}
              disabled={props.somenteLeitura}
              onChange={(e) => {
                handleComboChange(fieldKey, e.target.value, e.target.options[e.target.selectedIndex].text)
              }}
              value={formik.values[fieldKey] ?? ""}
            >
              <option value="">Selecione</option>
              {value.Opcao?.map((item) => (
                <option 
                  key={item.Codigo} 
                  value={item.Codigo}
                  id={`FichaDeCrediarioCliente_Select_${fieldKey}_${item.Codigo}`}
                  data-key={`select-option-${fieldKey}-${item.Codigo}`}
                >
                  {item.Nome}
                </option>
              ))}
            </select>
          )

        } 
        
        return (
          <input
            type="text"
            maxLength={254}
            style={{ paddingTop: "5px", height: "25px" }}
            name={fieldKey}
            id={`FichaDeCrediarioCliente_InputTexto_${fieldKey}`}
            disabled={props.somenteLeitura}
            onChange={(e) => {
              handleFieldChange(fieldKey, value.Tipo, e.target.value)
            }}
            value={formik.values[fieldKey] ?? ""}
          />
        )
      
    }
  }

  return (
    <div data-testid="ficha-crediario-container">
      <form
        style={{ paddingRight: '10px' }}
        id="FichaDeCrediario_Cliente"
        data-key="ficha-crediario-form"
      >
        {/* Seção de Dados do Cliente */}
        <div
          className={estiloFichaCrediario.form}
          id="FichaDeCrediarioCliente_DadosCliente"
          data-key="client-data-section"
        >
          <label className={estiloFichaCrediario.primeiraColuna}>
            CPF/CNPJ
          </label>
          <div
            style={{ paddingTop: "5px" }}
            id="FichaDeCrediarioCliente_CpfCnpj"
            data-key="client-cpf-cnpj"
          >
            {props.cpfCnpj}
          </div>
          
          <label
            className={estiloFichaCrediario.primeiraColuna}
            id="FichaDeCrediarioCliente_Nome"
            data-key="client-name-label"
          >
            Nome
          </label>
          <div
            style={{ paddingTop: "5px" }}
            id="FichaDeCrediarioCliente_NomeValor"
            data-key="client-name-value"
          >
            {props.nome}
          </div>
        </div>
        
        <br />
        
        {/* Seção de Dados da Ficha de Crediário */}
        <div
          className={estiloFichaCrediario.form}
          id="FichaDeCrediarioCliente_DadosFichaCrediario"
          data-key="credit-data-section"
        >
          {props.dadosFichaCrediario.map((value) => (
            <React.Fragment key={`field-group-${value.CodigoDadoAdicional}`}>
              <label
                className={estiloFichaCrediario.primeiraColuna}
                id={`FichaDeCrediarioCliente_Descricao_${value.CodigoDadoAdicional}`}
                data-key={`field-label-${value.CodigoDadoAdicional}`}
              >
                {value.Descricao}
              </label>
              {renderField(value)}
            </React.Fragment>
          ))}
        </div>
        
        {/* Botão de Salvar (condicional) */}
        {!props.somenteLeitura && (
          <button
            className={estiloFichaCrediario.botaoSalvar}
            id="FichaDeCrediarioCliente_botaoSalvar"
            data-key="save-button"
            type="button"
            onClick={() => props.onSalvar(formik.values, codigosAlterados)}
            disabled={props.processando}
          >
            {props.processando ? "Aguarde..." : "Salvar"}
          </button>
        )}
      </form>
      
      <section style={{ marginBottom: "100px" }} />
    </div>
  )
}